/**
 * Privacy-conscious product analytics for datasyte.ai.
 *
 * What this file sends: an event name, a short non-identifying label, a page
 * path, the referrer, and a small allow-listed set of numeric/enum properties.
 *
 * What it never sends: form field values, message bodies, email addresses,
 * names, phone numbers, free-text search terms, health information, or anything
 * that could describe a patient or an event. Helpers below sanitize props
 * before they leave the browser, and the `track-event` function enforces the
 * event allow-list server-side.
 *
 * Every event name and trigger is documented in `docs/analytics-events.md`.
 */

const FUNCTIONS_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

/** Runs work off the critical path so telemetry never competes with LCP. */
const whenIdle = (fn: () => void) => {
  if (typeof window === "undefined") return;
  const run = () => {
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
      .requestIdleCallback;
    if (ric) ric(fn, { timeout: 3000 });
    else window.setTimeout(fn, 1200);
  };
  if (document.readyState === "complete") run();
  else window.addEventListener("load", run, { once: true });
};

const post = (fn: string, payload: unknown) => {
  const url = `${FUNCTIONS_BASE}/${fn}`;
  const body = JSON.stringify(payload);
  whenIdle(() => {
    try {
      void fetch(url, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
        body,
      });
    } catch {
      /* telemetry must never break the page */
    }
  });
};

export type SiteEvent =
  /* Navigation and funnel */
  | "page_view"
  | "funnel_stage"
  | "nav_click"
  /* Demo intent and conversion */
  | "demo_cta_click"
  | "demo_scheduled"
  | "demo_form_start"
  | "demo_form_validation_error"
  | "demo_request_submitted"
  | "contact_email_click"
  | "contact_call_click"
  /* Product and platform engagement */
  | "product_tour_step"
  | "product_tour_complete"
  | "video_start"
  | "video_complete"
  /* Content engagement */
  | "article_read_progress"
  | "resource_filter_used"
  | "resource_download"
  | "technical_document_request"
  /* Educational assessment (Resuscitation Readiness Index) */
  | "assessment_start"
  | "assessment_complete"
  | "assessment_summary_download"
  | "assessment_report_saved"
  /* Executive value model (Resuscitation Operations Value Model) */
  | "value_model_adjusted"
  | "value_model_download"
  | "value_model_print"
  | "value_model_saved"
  /* Trust Center document requests */
  | "trust_document_request_start"
  | "trust_document_request_submitted"
  /* Strategic partnerships */
  | "partnership_inquiry_start"
  | "partnership_inquiry_submitted"
  /* Role and goal personalization (explicit, visitor-selected) */
  | "personalization_prompt_shown"
  | "personalization_selected"
  | "personalization_cleared"
  | "personalization_dismissed"
  /* Outbound */
  | "outbound_click"
  | "clinical_source_click";

/** Conversion funnel. Stages are ordered; a session can skip stages. */
export type FunnelStage =
  | "landing"
  | "platform_engagement"
  | "product_engagement"
  | "evidence_review"
  | "demo_intent"
  | "demo_submission";

export const FUNNEL_STAGES: FunnelStage[] = [
  "landing",
  "platform_engagement",
  "product_engagement",
  "evidence_review",
  "demo_intent",
  "demo_submission",
];

/** Props are limited to short scalars — no free text, no identifiers. */
const ALLOWED_PROP_KEYS = new Set([
  "stage",
  "stage_index",
  "step",
  "step_count",
  "percent",
  "seconds",
  "seconds_away",
  "duration_seconds",
  "host",
  "kind",
  "topic",
  "audience",
  "type",
  "sort",
  "count",
  "from_path",
  "asset",
  "surface",
]);

const sanitizeProps = (props?: Record<string, unknown>) => {
  if (!props) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(props)) {
    if (!ALLOWED_PROP_KEYS.has(key)) continue;
    if (typeof value === "number" || typeof value === "boolean") out[key] = value;
    else if (typeof value === "string") out[key] = value.slice(0, 80);
  }
  return Object.keys(out).length ? out : undefined;
};

/** Labels identify a surface or a slug — never user-entered content. */
const sanitizeLabel = (label?: string) =>
  typeof label === "string" ? label.replace(/\s+/g, " ").trim().slice(0, 120) : undefined;

export const trackEvent = (
  event_name: SiteEvent,
  label?: string,
  props?: Record<string, unknown>,
) => {
  post("track-event", {
    event_name,
    label: sanitizeLabel(label),
    props: sanitizeProps(props),
    // Path only — query strings can carry campaign or form values.
    page_path: typeof location !== "undefined" ? location.pathname : null,
    referrer:
      typeof document !== "undefined" && document.referrer
        ? document.referrer.split("?")[0].slice(0, 300)
        : null,
  });
};

/* ------------------------------------------------------------------ */
/* Conversion funnel                                                   */
/* ------------------------------------------------------------------ */

const FUNNEL_KEY = "funnel_stage_reached";

/** Maps a route to the funnel stage it represents. */
export const stageForPath = (path: string): FunnelStage | null => {
  if (path === "/") return "landing";
  if (path === "/request-demo" || path === "/demo") return "demo_intent";
  if (path.startsWith("/evidence") || path.startsWith("/resources") || path === "/solutions/it-security-integration")
    return "evidence_review";
  if (path === "/aimediq" || path.startsWith("/solutions")) return "product_engagement";
  if (path === "/datasyteai" || path === "/platform" || path === "/how-it-works")
    return "platform_engagement";
  return null;
};

/** Records the furthest funnel stage once per session per stage. */
export const trackFunnelStage = (stage: FunnelStage) => {
  try {
    const seen = new Set<string>(JSON.parse(sessionStorage.getItem(FUNNEL_KEY) ?? "[]"));
    if (seen.has(stage)) return;
    seen.add(stage);
    sessionStorage.setItem(FUNNEL_KEY, JSON.stringify([...seen]));
  } catch {
    /* private mode — still report the stage */
  }
  trackEvent("funnel_stage", stage, { stage, stage_index: FUNNEL_STAGES.indexOf(stage) });
};

/* ------------------------------------------------------------------ */
/* Engagement helpers                                                  */
/* ------------------------------------------------------------------ */

/** Video playback. `asset` is a filename, never a caption or transcript. */
export const trackVideoStart = (asset: string, surface?: string) =>
  trackEvent("video_start", asset, { asset, surface });

export const trackVideoComplete = (asset: string, seconds?: number) =>
  trackEvent("video_complete", asset, { asset, duration_seconds: seconds ? Math.round(seconds) : undefined });

/** Product tour / interface walkthrough steps. */
export const trackTourStep = (tour: string, step: number, stepCount: number) => {
  trackEvent("product_tour_step", tour, { step, step_count: stepCount });
  if (step >= stepCount) trackEvent("product_tour_complete", tour, { step_count: stepCount });
};

/** Article reading depth: fires once at 25 / 50 / 75 / 100 percent. */
export const trackArticleProgress = (slug: string, percent: number) =>
  trackEvent("article_read_progress", slug, { percent });

/** Filter and sort usage on the resource hub — enum values only, no search text. */
export const trackResourceFilter = (facet: string, value: string) =>
  trackEvent("resource_filter_used", facet, { kind: facet, type: value });

export const trackResourceDownload = (asset: string) =>
  trackEvent("resource_download", asset, { asset });

export const trackTechnicalDocRequest = (surface: string) =>
  trackEvent("technical_document_request", surface, { surface });

/** Outbound link. Only the host is recorded, never the full URL with params. */
export const trackOutbound = (href: string, clinicalSource = false) => {
  let host = "";
  try {
    host = new URL(href).host;
  } catch {
    return;
  }
  trackEvent(clinicalSource ? "clinical_source_click" : "outbound_click", host, { host });
};

export const trackNavClick = (label: string, fromPath: string) =>
  trackEvent("nav_click", label, { from_path: fromPath });

/** Reports CSP / Trusted Types violations from real sessions. */
export const initCspReporting = () => {
  if (typeof document === "undefined") return;
  document.addEventListener("securitypolicyviolation", (e) => {
    post("csp-report", {
      "document-uri": e.documentURI,
      "blocked-uri": e.blockedURI,
      "violated-directive": e.violatedDirective,
      "effective-directive": e.effectiveDirective,
      disposition: e.disposition,
      "source-file": e.sourceFile,
      "line-number": e.lineNumber,
      "column-number": e.columnNumber,
      "script-sample": e.sample,
    });
  });
};

const DEMO_CLICK_KEY = "demo_cta_click_at";

/** Track a Schedule-a-Demo click and remember it for conversion measurement. */
export const trackDemoClick = (location: string) => {
  try {
    sessionStorage.setItem(DEMO_CLICK_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  trackFunnelStage("demo_intent");
  trackEvent("demo_cta_click", location);
};

/**
 * Records a successful demo booking. Fires when the scheduler redirects back
 * with ?demo=scheduled, or when the visitor returns to the tab after spending
 * long enough in the scheduler to complete a booking.
 */
export const initDemoConversionTracking = () => {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  if (params.get("demo") === "scheduled") {
    trackEvent("demo_scheduled", "redirect_confirmed");
    trackFunnelStage("demo_submission");
    try {
      sessionStorage.removeItem(DEMO_CLICK_KEY);
    } catch {
      /* ignore */
    }
    return;
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    try {
      const at = Number(sessionStorage.getItem(DEMO_CLICK_KEY));
      if (!at) return;
      const elapsed = Date.now() - at;
      sessionStorage.removeItem(DEMO_CLICK_KEY);
      if (elapsed > 45_000 && elapsed < 30 * 60_000) {
        trackEvent("demo_scheduled", "return_after_scheduler", { seconds_away: Math.round(elapsed / 1000) });
        trackFunnelStage("demo_submission");
      }
    } catch {
      /* ignore */
    }
  });
};
